// Minimal ZIP writer (STORE method, no compression) — enough to package an
// Office Open XML document in the browser without pulling a zip library.
// Every reader accepts stored entries; the payload is a few KB of XML.

const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        table[n] = c >>> 0;
    }
    return table;
})();

function crc32(bytes) {
    let crc = 0xffffffff;
    for (let i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
    return (crc ^ 0xffffffff) >>> 0;
}

function dosDateTime(date) {
    const time = (date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1);
    const day = ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
    return { time, day };
}

/**
 * @param {Array<{ name: string, data: string|Uint8Array }>} entries  in archive order
 * @param {string} type  MIME type of the resulting Blob
 * @returns {Blob}
 */
export function zipStore(entries, type = "application/zip") {
    const encoder = new TextEncoder();
    const stamp = dosDateTime(new Date());
    const locals = [];
    const centrals = [];
    let offset = 0;

    for (const { name, data } of entries) {
        const nameBytes = encoder.encode(name);
        const body = typeof data === "string" ? encoder.encode(data) : data;
        const crc = crc32(body);

        const local = new DataView(new ArrayBuffer(30));
        local.setUint32(0, 0x04034b50, true); // local file header signature
        local.setUint16(4, 20, true); // version needed
        local.setUint16(6, 0x0800, true); // flags: UTF-8 names
        local.setUint16(8, 0, true); // method: store
        local.setUint16(10, stamp.time, true);
        local.setUint16(12, stamp.day, true);
        local.setUint32(14, crc, true);
        local.setUint32(18, body.length, true); // compressed size
        local.setUint32(22, body.length, true); // uncompressed size
        local.setUint16(26, nameBytes.length, true);
        local.setUint16(28, 0, true); // extra length
        locals.push(new Uint8Array(local.buffer), nameBytes, body);

        const central = new DataView(new ArrayBuffer(46));
        central.setUint32(0, 0x02014b50, true); // central directory signature
        central.setUint16(4, 20, true); // version made by
        central.setUint16(6, 20, true); // version needed
        central.setUint16(8, 0x0800, true);
        central.setUint16(10, 0, true);
        central.setUint16(12, stamp.time, true);
        central.setUint16(14, stamp.day, true);
        central.setUint32(16, crc, true);
        central.setUint32(20, body.length, true);
        central.setUint32(24, body.length, true);
        central.setUint16(28, nameBytes.length, true);
        central.setUint16(30, 0, true); // extra length
        central.setUint16(32, 0, true); // comment length
        central.setUint16(34, 0, true); // disk number
        central.setUint16(36, 0, true); // internal attributes
        central.setUint32(38, 0, true); // external attributes
        central.setUint32(42, offset, true); // local header offset
        centrals.push(new Uint8Array(central.buffer), nameBytes);

        offset += 30 + nameBytes.length + body.length;
    }

    const centralSize = centrals.reduce((total, part) => total + part.length, 0);
    const end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, 0x06054b50, true); // end of central directory signature
    end.setUint16(4, 0, true);
    end.setUint16(6, 0, true);
    end.setUint16(8, entries.length, true);
    end.setUint16(10, entries.length, true);
    end.setUint32(12, centralSize, true);
    end.setUint32(16, offset, true);
    end.setUint16(20, 0, true);

    return new Blob([...locals, ...centrals, new Uint8Array(end.buffer)], { type });
}
